import React from 'react';
import { MenuItem } from '../Sidebar';
import { getModuleKeyForPath } from '../../../config/permissionModules';
import { VESSEL_SIDEBAR_MODULES } from '../../../vessel-modules/vesselSidebarRegistry';
import {
  getModuleGroupKeysForPath,
  isModuleGroupEnabled,
  ModuleEntitlements,
} from '../../../modules/admin/module-management/config';

const ListIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const VesselSupplyRequisitionsIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h6m-7 4h8M8 13h8M8 17h5M7 3h10a2 2 0 012 2v14a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" />
  </svg>
);

const VesselNewItemIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const VesselPendingDeliveriesIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
    />
  </svg>
);

const VesselDashboardIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2 7-7 7 7 2 2M5 10v10h14V10" />
  </svg>
);

function vesselModuleListIcon(pathSegment: string): React.ReactNode {
  if (pathSegment === 'dashboard') return VesselDashboardIcon;
  if (pathSegment === 'requisitions' || pathSegment === 'spare-requisitions') return VesselSupplyRequisitionsIcon;
  if (pathSegment === 'pending-deliveries' || pathSegment === 'spare-pending-deliveries') {
    return VesselPendingDeliveriesIcon;
  }
  return ListIcon;
}

/** Same cubes glyph as office `SuppliesIcon` / Purchasing group. */
const VesselModulesGroupIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
    />
  </svg>
);

function buildGroupChildren(group: 'supplies' | 'spares' | 'crew'): MenuItem[] {
  const children: MenuItem[] = [];
  for (const mod of VESSEL_SIDEBAR_MODULES) {
    if (mod.group !== group) continue;
    if (mod.pathSegment === 'dashboard') continue;
    const listPath = `/${mod.pathSegment}`;
    if (mod.newLink) {
      children.push({
        name: mod.newLink.label,
        path: `${listPath}${mod.newLink.pathSuffix}`,
        permissionModuleKey: mod.permissionModuleKey,
        icon: VesselNewItemIcon,
      });
    }
    children.push({
      name: mod.listLabel,
      path: listPath,
      permissionModuleKey: mod.permissionModuleKey,
      icon: vesselModuleListIcon(mod.pathSegment),
    });
  }
  return children;
}

/**
 * Menu shape for User Permissions (kept for shared helpers; vessel app has no admin UI).
 */
export function getVesselModulesMenuForPermissions(): MenuItem {
  return {
    name: 'Supplies',
    icon: VesselModulesGroupIcon,
    children: buildGroupChildren('supplies'),
  };
}

export function buildVesselModulesSidebarGroup(_vesselAccountId?: number | null): MenuItem {
  return {
    name: 'Supplies',
    moduleGroupKey: 'shipping',
    icon: VesselModulesGroupIcon,
    defaultExpanded: true,
    children: buildGroupChildren('supplies'),
  };
}

export function buildVesselSparesSidebarGroup(_vesselAccountId?: number | null): MenuItem {
  return {
    name: 'Spares',
    moduleGroupKey: 'spares',
    icon: VesselModulesGroupIcon,
    defaultExpanded: true,
    children: buildGroupChildren('spares'),
  };
}

export function buildVesselCrewSidebarGroup(_vesselAccountId?: number | null): MenuItem {
  return {
    name: 'Crew',
    moduleGroupKey: 'hr',
    icon: VesselModulesGroupIcon,
    defaultExpanded: true,
    children: buildGroupChildren('crew'),
  };
}

function buildVesselDashboardItem(): MenuItem {
  return {
    name: 'Dashboard',
    path: '/dashboard',
    permissionModuleKey: 'vessel_dashboard',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2 7-7 7 7 2 2M5 10v10h14V10" />
      </svg>
    ),
    views: [{ type: 'list', label: 'Dashboard', path: '/dashboard', icon: VesselDashboardIcon }],
  };
}

function getItemModuleKey(item: MenuItem): string | undefined {
  if (item.permissionModuleKey) return item.permissionModuleKey;
  if (item.path) return getModuleKeyForPath(item.path);
  return undefined;
}

function itemAllowedByPermission(item: MenuItem, canView: (moduleKey: string) => boolean): boolean {
  if (item.sectionLabel) return true;
  if (item.name === 'Bookmarks') return true;
  const key = getItemModuleKey(item);
  if (!key) {
    if (item.children?.length) return true;
    return Boolean(item.path);
  }
  return canView(key);
}

function itemAllowedByEntitlement(
  item: MenuItem,
  entitlements: ModuleEntitlements | null | undefined,
): boolean {
  if (item.sectionLabel) return true;
  if (item.name === 'Bookmarks' || item.name === 'Dashboard') return true;
  if (!entitlements) return true;

  if (item.moduleGroupKey) {
    return isModuleGroupEnabled(item.moduleGroupKey, entitlements);
  }

  const path = item.path ?? item.views?.[0]?.path;
  if (path) {
    const keys = getModuleGroupKeysForPath(path);
    if (keys.length === 0) return true;
    return keys.some((k) => isModuleGroupEnabled(k, entitlements));
  }

  return true;
}

function filterChildrenByAccess(
  children: MenuItem[] | undefined,
  canView: ((moduleKey: string) => boolean) | undefined,
  entitlements: ModuleEntitlements | null | undefined,
): MenuItem[] | undefined {
  if (!children) return undefined;
  const filtered = children
    .filter(
      (child) =>
        itemAllowedByEntitlement(child, entitlements) &&
        (!canView || itemAllowedByPermission(child, canView)),
    )
    .map((child) => ({
      ...child,
      children: filterChildrenByAccess(child.children, canView, entitlements),
    }))
    .filter((child) => {
      if (child.sectionLabel) return true;
      const hasContent =
        !!child.path || (child.children && child.children.length > 0) || (child.views && child.views.length > 0);
      return hasContent;
    });
  return filtered.length > 0 ? filtered : undefined;
}

function itemVisibleAfterFilter(item: MenuItem): boolean {
  if (item.sectionLabel) return true;
  if (item.name === 'Bookmarks') return true;
  return !!(item.path || item.children?.length || item.views?.length);
}

/** Vessel portal sidebar: dashboard above Supplies and Spares. */
export const getSidebarItems = (
  canView?: (moduleKey: string) => boolean,
  vesselAccountId?: number | null,
  entitlements?: ModuleEntitlements | null,
): MenuItem[] => {
  let result: MenuItem[] = [
    {
      name: 'Bookmarks',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      ),
      children: [],
    },
    buildVesselDashboardItem(),
    buildVesselModulesSidebarGroup(vesselAccountId),
    buildVesselSparesSidebarGroup(vesselAccountId),
    buildVesselCrewSidebarGroup(vesselAccountId),
  ];

  if (canView || entitlements) {
    result = result
      .filter(
        (item) =>
          itemAllowedByEntitlement(item, entitlements) &&
          (!canView || itemAllowedByPermission(item, canView)),
      )
      .map((item) => ({
        ...item,
        children: filterChildrenByAccess(item.children, canView, entitlements),
      }))
      .filter(itemVisibleAfterFilter);
  }
  return result;
};

export function flattenSidebarItemsForSearch(
  items: MenuItem[],
  parentTrail: string[] = [],
): { label: string; path: string; group?: string; icon?: React.ReactNode }[] {
  const out: { label: string; path: string; group?: string; icon?: React.ReactNode }[] = [];
  for (const item of items) {
    if (item.sectionLabel) continue;
    if (item.name === 'Bookmarks') continue;

    const path = item.path ?? item.views?.[0]?.path;
    const breadcrumb = parentTrail.length > 0 ? parentTrail.join(' › ') : undefined;

    if (path) {
      out.push({ label: item.name, path, group: breadcrumb, icon: item.icon });
    }

    if (item.children?.length) {
      const childTrail = [...parentTrail, item.name];
      for (const child of item.children) {
        const childPath = child.path ?? child.views?.[0]?.path;
        const childBreadcrumb = childTrail.join(' › ');
        if (childPath) {
          out.push({ label: child.name, path: childPath, group: childBreadcrumb, icon: child.icon });
        }
        if (child.children?.length) {
          out.push(...flattenSidebarItemsForSearch(child.children, childTrail));
        }
      }
    }
  }

  return out;
}

/**
 * Top-menu roots. Vessel menus usually have no sectionLabel dividers — keep flat module groups.
 * When office-style sections exist, collapse into General / Work / Reference Data / System.
 */
export function buildTopMenuRoots(items: MenuItem[]): MenuItem[] {
  if (!items.some((i) => i.sectionLabel)) {
    return items.filter((i) => i.name !== 'Bookmarks' && !i.sectionLabel);
  }

  const general: MenuItem[] = [];
  const work: MenuItem[] = [];
  let reference: MenuItem | null = null;
  let system: MenuItem | null = null;
  let bucket: 'general' | 'work' | 'reference' | 'system' = 'general';

  for (const item of items) {
    if (item.name === 'Bookmarks') continue;

    const section = (item.sectionLabel || '').trim().toLowerCase();
    if (section === 'general') {
      bucket = 'general';
      continue;
    }
    if (section === 'work') {
      bucket = 'work';
      continue;
    }
    if (section === 'reference' || section === 'reference data') {
      bucket = 'reference';
      continue;
    }
    if (section === 'system') {
      bucket = 'system';
      continue;
    }
    if (item.sectionLabel) continue;

    if (bucket === 'general') general.push(item);
    else if (bucket === 'work') work.push(item);
    else if (bucket === 'reference') {
      reference = {
        ...item,
        name: 'Reference Data',
        children: item.children?.length ? item.children : [item],
      };
    } else if (bucket === 'system') {
      system = item;
    }
  }

  const roots: MenuItem[] = [];
  if (general.length > 0) {
    roots.push({ name: 'General', children: general });
  }
  if (work.length > 0) {
    roots.push({ name: 'Work', children: work });
  }
  if (reference) roots.push(reference);
  if (system) roots.push(system);
  return roots;
}

export const sidebarItems: MenuItem[] = getSidebarItems();
